/**
 * German message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: informal du (locked in l10n/locales.md).
 *
 * Platform terms follow German UI conventions (Design for theme,
 * Unterhaltung for conversation, koppeln for pair, verwerfen for
 * dismiss); "Glance" is the untranslated feature brand name
 * (glossary D1) and "Clip"/"Clips" is a locked loanword (D2).
 *
 * Plural notes: German is a {one, other} CLDR locale. Nouns that are
 * invariant across both forms (Treffer, Zeichen) use a plain template;
 * everything else selects through `selectPlural("de", …)`.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Verbunden",
    statusReconnecting: "Verbindung wird wiederhergestellt…",
    statusDisconnected: "Getrennt",
    openSettings: "Einstellungen öffnen",
    pairedFallbackDevice: "dein Telefon",
    publicModeToggleEnable: "Benachrichtigungsinhalte ausblenden",
    publicModeToggleDisable: "Benachrichtigungsinhalte anzeigen",
  },

  notifications: {
    replyAriaLabel: "Antworten",
    replyPlaceholder: "Antworten…",
    replySend: "Senden",
    sent: "Gesendet",
    failedToSend: "Senden fehlgeschlagen",
    unread: "Ungelesen",
    dismiss: "Verwerfen",
    notificationImageAlt: "Benachrichtigungsbild",
  },

  conversations: {
    replyAriaLabel: "Antworten",
    replyPlaceholder: "Antworten…",
    replySend: "Senden",
    sent: "Gesendet",
    failedToSend: "Senden fehlgeschlagen",
    dismiss: "Unterhaltung verwerfen",
    messagesListLabel: "Nachrichten in dieser Unterhaltung",
    deviceOwner: "Du",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Keine ungelesenen Nachrichten"
        : `${count} ${selectPlural("de", count, {
            one: "ungelesene Nachricht",
            other: "ungelesene Nachrichten",
          })}`,
  },

  list: {
    today: "Heute",
    yesterday: "Gestern",
    searchNotifications: "Benachrichtigungen durchsuchen",
    clearSearch: "Suche löschen",
    clearAll: "Alle verwerfen",
    markAllRead: "Alle als gelesen markieren",
    groupCheckboxLabel: "Gruppieren",
    noNotificationsTitle: "Noch keine Benachrichtigungen",
    noNotificationsDesc: "Neue von deinem Telefon gespiegelte Benachrichtigungen erscheinen hier.",
    noMatchesTitle: "Keine Treffer",
    notificationsList: "Benachrichtigungen",
    privacyAppList: "Benachrichtigungen nach App",
    clear: "Verwerfen",
    // "Treffer" is invariant across German plural forms — plain template.
    clearMatches: (count: number): string => `${count} Treffer verwerfen`,
    noMatchesDesc: (query: string): string => `Keine Benachrichtigungen entsprechen „${query}“.`,
    appNotificationsGroup: (appName: string): string => `Benachrichtigungen von ${appName}`,
    dismissAllFrom: (appName: string): string => `Alle von ${appName} verwerfen`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("de", count, { one: "Nachricht", other: "Nachrichten" })
          : kind === "calls"
            ? selectPlural("de", count, { one: "Anruf", other: "Anrufe" })
            : selectPlural("de", count, { one: "Benachrichtigung", other: "Benachrichtigungen" });
      return `${count} ${noun}`;
    },
    kbdDismiss: "verwerfen",
    kbdNavigate: "navigieren",
    kbdOrSeparator: "oder",
  },

  settings: {
    back: "Zurück zu den Benachrichtigungen",
    settings: "Einstellungen",
    general: "Allgemein",
    theme: "Design",
    themeAuto: "Automatisch",
    themeLight: "Hell",
    themeDark: "Dunkel",
    language: "Sprache",
    languageSystemDefault: "Systemstandard",
    notificationsSection: "Benachrichtigungen",
    osNotifications: "Systembenachrichtigungen",
    callNotifications: "Anrufbenachrichtigungen",
    privacySecurity: "Datenschutz",
    analyticsToggle: "Anonyme Nutzungsstatistiken",
    analyticsToggleDescription: "Nur Zahlen und Meilensteine — nie deine Benachrichtigungen.",
    statusConnected: "Verbunden",
    statusReconnecting: "Verbindung wird wiederhergestellt…",
    statusDisconnected: "Getrennt",
    disconnectFromPhone: "Vom Telefon trennen",
    disconnectConfirm: "Bist du sicher? Dadurch wird dein Gerät entkoppelt.",
    yesDisconnect: "Ja, trennen",
    cancel: "Abbrechen",
    version: "Version",
    encryption: "Verschlüsselung",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Brauchst du Hilfe? Schreib an __EMAIL__",
    disconnectFailed: "Trennen fehlgeschlagen — bitte versuche es erneut",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Getrennt, aber die lokale Bereinigung ist fehlgeschlagen: ${errorMsg}. Bitte lade die Seite neu.`,
    disconnectLocalCleanupFailed:
      "Getrennt, aber die lokale Bereinigung ist fehlgeschlagen. Bitte lade die Seite neu.",
  },

  pairing: {
    enterCodeInstruction: "Gib diesen Code in der Reflecto-App ein",
    pairedSuccessfully: "Erfolgreich gekoppelt!",
    pairingFailed: "Kopplung fehlgeschlagen",
    requestingPairingCode: "Kopplungscode wird angefordert…",
    codeExpired: "Code abgelaufen",
    tryAgain: "Erneut versuchen",
    failedToStartPairing: "Kopplung konnte nicht gestartet werden",
    installHeading: "Du hast die App noch nicht?",
    installScanHint: "Mit der Kamera deines Telefons scannen",
    installQrLabel: "QR-Code zur Installation von Reflecto auf deinem Telefon",
    capabilitiesHeading: "Nach der Kopplung kannst du:",
    capabilityMirror: "Benachrichtigungen deines Telefons hier sehen",
    capabilityReply: "Antworten, ohne zum Telefon zu greifen",
    capabilityShare: "Zwischenablage und Dateien in beide Richtungen teilen",
    encryptionNote: "Ende-zu-Ende-verschlüsselt. Der Server sieht deine Inhalte nie.",
  },

  dnd: {
    title: "Nicht stören",
    description: "Pausiere Benachrichtigungen auf diesem Gerät für die nächste Stunde.",
    snooze1h: "1 Stunde pausieren",
    snoozeActiveUntil: (formattedTime: string): string => `Aktiv bis ${formattedTime}`,
    cancelSnooze: "Abbrechen",
  },

  connectors: {
    title: "Verbundene Dienste",
    emptyBody:
      "Noch keine verbundenen Dienste. Verbinde ChatGPT oder Claude über deren Connector-Einstellungen und bestätige dann den Code auf deinem Telefon.",
    scopeSend: "Kann Benachrichtigungen senden",
    added: (date: string): string => `Hinzugefügt ${date}`,
    lastUsed: (relative: string): string => `Zuletzt verwendet ${relative}`,
    neverUsed: "Nie verwendet",
    revoke: "Trennen",
    revokeConfirm: (label: string): string => `${label} trennen? Der Zugriff endet sofort.`,
    revokeAction: "Trennen",
    revokeConfirmLabel: (label: string): string => `${label} trennen`,
    cancel: "Abbrechen",
    loadError: "Verbundene Dienste konnten nicht geladen werden. Bitte versuche es erneut.",
    revokeError: "Der Dienst konnte nicht getrennt werden. Bitte versuche es erneut.",
    retry: "Wiederholen",
  },

  safetyNumber: {
    rowLabel: "Verschlüsselung überprüfen",
    rowSublabel: "Vergleichen Sie Sicherheitsnummern mit Ihrem Telefon",
    cardTitle: "Sicherheitsnummer",
    caption: (deviceLabel: string): string =>
      `Vergleichen Sie mit der auf ${deviceLabel} angezeigten Nummer. Wenn beide übereinstimmen, ist Ihre End-to-End-Verschlüsselung intakt.`,
    repairNote: "Diese Nummer ändert sich, wenn Sie ein Gerät erneut koppeln.",
    copy: "Kopieren",
    copied: "Kopiert",
    maskedHint: "Verborgen, während der Datenschutzmodus aktiv ist",
  },

  feedback: {
    title: "Feedback senden",
    close: "Schließen",
    messageLabel: "Deine Nachricht",
    messagePlaceholder:
      "Einen Bug gefunden, eine Idee, oder fühlt sich etwas falsch an? Erzähl es uns.",
    categoryLabel: "Kategorie",
    categoryBug: "Bug",
    categoryIdea: "Idee",
    categoryOther: "Sonstiges",
    emailLabel: "E-Mail (optional, nur falls du eine Antwort möchtest)",
    emailPlaceholder: "name@beispiel.de",
    emailHelp: "Wir setzen dich auf keine Liste.",
    disclosure:
      "Deine Nachricht wird an den Reflecto-Server gesendet und in unserem internen Issue-Tracker gespeichert. Wir hängen deine App-Version und eine bei der Kopplung vergebene Geräte-ID an, um Berichte zuordnen zu können. Wir sehen weder deine Benachrichtigungen noch Kontakte oder andere persönliche Daten. Die E-Mail-Adresse ist optional — gib sie nur an, wenn du eine Antwort möchtest. Wir setzen dich auf keine Liste.",
    submit: "Feedback senden",
    sending: "Wird gesendet…",
    success: "Danke — wird von einem Menschen gelesen, meist innerhalb von 24 h",
    error: "Senden fehlgeschlagen. Bitte versuche es erneut.",
    done: "Fertig",
    sendFeedback: "Feedback senden",
  },

  uninstall: {
    headline: "Die Reflecto-Erweiterung ist deinstalliert",
    body: "Es gab nie ein Konto, und die Erweiterung hat deine Kopplung auf diesem Computer behalten — es bleibt also nichts, was du aufräumen müsstest.",
    ask: "Wenn du dreißig Sekunden hast, würden wir gern wissen, was schiefgelaufen ist.",
    categoryLegend: "Um welche Art von Feedback geht es?",
    categoryBug: "Etwas war kaputt",
    categoryIdea: "Mir hat etwas gefehlt, das ich brauche",
    categoryOther: "Etwas anderes",
    messageLabel: "Was ist schiefgelaufen?",
    messagePlaceholder: "Nach ein paar Stunden kamen keine Benachrichtigungen mehr an.",
    messageRequired: "Schreib erst einen Satz, dann sende.",
    charactersLeft: (count: number): string => `Noch ${count} Zeichen`,
    emailLabel: "E-Mail (optional)",
    emailPlaceholder: "name@beispiel.de",
    emailHelp: "Wird nur genutzt, um dir zu antworten. Sonst nichts.",
    submit: "Feedback senden",
    sending: "Wird gesendet…",
    retry: "Erneut versuchen",
    privacyNote:
      "Dieses Formular ist mit keinem Gerät verknüpft. Es enthält weder deine Benachrichtigungen noch deine Zwischenablage oder Dateien. Die E-Mail-Adresse ist optional und wird nur für eine Antwort verwendet.",
    success: "Danke. Jede einzelne davon liest ein Mensch.",
    errorGeneric: "Das wurde nicht gesendet. Deine Nachricht ist noch da.",
    errorRateLimited:
      "Du hast in letzter Zeit mehrere Nachrichten gesendet. Versuch es in etwa einer Stunde erneut. Dein Text bleibt erhalten.",
    footerWebApp: "Die Reflecto-Web-App funktioniert auch ohne die Erweiterung.",
    footerWebAppLink: "Web-App öffnen",
  },

  glance: {
    // Brand term (glossary D1): the feature name "Glance" is the heading
    // itself in non-English locales — the EN "At a glance" wordplay is
    // EN-only.
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Schließen",
    aiSummaryTag: "KI-Zusammenfassung",
    errorAi: "Zusammenfassung konnte gerade nicht erstellt werden.",
    retryCta: "Erneut versuchen",
    downloadHeading: "KI-Zusammenfassungen erhalten",
    downloadBody:
      "Verschaffe dir mit einer KI-Zusammenfassung direkt auf dem Gerät einen Überblick über Verpasstes. Läuft vollständig auf deinem Gerät.",
    downloadCta: "Modell herunterladen",
    downloadPrivacyNote: "Einmaliger Download. Der Server sieht deine Benachrichtigungen nie.",
    downloadingHeading: "Modell wird heruntergeladen…",
    caughtUp: "Alles erledigt — gerade braucht dich nichts.",
    needsYou: "Braucht dich",
    quiet: "Ruhig",
    scanning: (pending: number): string => `Fasse ${pending} zusammen…`,
    jump: "Benachrichtigung öffnen",
    affordanceReply: "Antworten",
    affordanceOpen: "Öffnen",
    affordanceCopyOtp: "Code kopieren",
    buttonLabel: "Glance",
  },

  update: {
    title: "Update verfügbar",
    description: "Eine neue Version von Reflecto ist bereit. Lade neu, um zu aktualisieren.",
    reload: "Neu laden",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Wir haben dieses Gerät „${name}“ genannt.`,
    cardLooksGood: "Passt",
    cardEdit: "Bearbeiten",
    cardAriaLabel: "Gerätenamen bestätigen",
    renameTitle: "Dieses Gerät umbenennen",
    renameLabel: "Gerätename",
    renameSave: "Speichern",
    renameCancel: "Abbrechen",
    renameFailed: "Umbenennen fehlgeschlagen. Bitte versuche es erneut.",
  },

  collision: {
    title: (name: string): string => `Du hast bereits ein „${name}“`,
    subtitle: "Wie sollen wir dieses hier nennen?",
    chipWork: (name: string): string => `Arbeit ${name}`,
    chipHome: (name: string): string => `Zuhause ${name}`,
    chipPersonal: (name: string): string => `Privat ${name}`,
    customLabel: "Eigener Name",
    save: "Speichern",
    useAnyway: "Trotzdem den ursprünglichen Namen verwenden",
    saveFailed: "Neuer Name konnte nicht gespeichert werden. Bitte versuche es erneut.",
    ariaLabel: "Doppelten Gerätenamen auflösen",
  },

  clipboard: {
    title: "Zwischenablage",
    emptyTitle: "Noch keine Clips",
    empty: "Clips von deinen anderen Geräten erscheinen hier. Zum Kopieren tippen.",
    from: (label: string): string => `von ${label}`,
    to: (label: string): string => `an ${label}`,
    copy: "In die Zwischenablage kopieren",
    copied: "Kopiert",
    reveal: "Anzeigen",
    hide: "Verbergen",
    maskedHint: "Inhalt verborgen – zum Anzeigen auf „Anzeigen“ tippen",
    sensitiveChip: "Vertraulich",
    cloudChip: "über dein API-Token",
    delete: "Clip löschen",
    sendPlaceholder: "Text zum Senden einfügen…",
    sendCta: "Senden",
    sendSensitiveToggle: "Als vertraulich markieren",
    sendSuccess: (n: number): string =>
      `An ${n} ${selectPlural("de", n, { one: "Gerät", other: "Geräte" })} gesendet.`,
    sendPartial: (n: number, total: number): string =>
      `${n}/${total} ${selectPlural("de", total, { one: "Gerät", other: "Geräten" })} erreicht.`,
    sendNoDevices: "Keine gekoppelten Geräte zum Senden.",
    sendFailed: "Senden fehlgeschlagen. Bitte versuche es erneut.",
    sendEmpty: "Nichts zu senden.",
    // "Zeichen" is invariant across German plural forms — plain template.
    dockRecentHeading: "Zuletzt",
    dockEmptyClipboard: "Deine Zwischenablage ist leer.",
    clearAll: "Alle verwerfen",
    clearAllConfirm: "Ja, alle verwerfen",
    clearAllCancel: "Abbrechen",
    shareTitle: "An deine Geräte senden",
    shareReview:
      "Prüfe, was du sendest. Es geht Ende-zu-Ende-verschlüsselt an deine gekoppelten Geräte.",
    shareSend: "Senden",
    shareCancel: "Abbrechen",
    shareNotPaired: "Kopple zuerst ein Gerät, um geteilten Text zu senden.",
    shareOpenApp: "Reflecto öffnen",
    pasteFromClipboard: "Aus Zwischenablage einfügen",
    readFailed: "Zwischenablage konnte nicht gelesen werden — füge den Text unten ein.",
  },
  install: {
    title: "Reflecto installieren",
    body: "Füge es zu deinem Startbildschirm hinzu, um schneller darauf zuzugreifen.",
    cta: "Installieren",
    dismissAria: "Installationshinweis verwerfen",
  },
  nav: {
    home: "Start",
    clipboard: "Zwischenablage",
    files: "Dateien",
    ariaLabel: "Hauptnavigation",
    fileHiddenPublicMode: "Ausgeblendet, solange der Privatmodus aktiv ist.",
  },
  common: {
    loading: "Wird geladen",
  },
  errors: {
    notPaired: "Nicht gekoppelt",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} auf ${os}`,
    webFallback: "Webbrowser",
  },
  palette: {
    placeholder: "Befehl eingeben…",
    noResults: "Keine passenden Befehle",
    ariaLabel: "Befehlspalette",
    clearAll: "Alle Benachrichtigungen verwerfen",
    glance: "Glance-Zusammenfassung",
    cancelSnooze: "Pause abbrechen",
    copyClip: "Letzten empfangenen Clip kopieren",
  },

  fileTransfer: {
    sendTitle: "Eine Datei senden",
    sendChooseFile: "Datei auswählen",
    sendDeviceLabel: "Senden an",
    sendPhoneFallbackName: "Telefon",
    send: "Datei senden",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Wird vorbereitet…" : `Wird vorbereitet – ${pct}%`,
    sendDelivering: "Wird an dein Telefon übermittelt…",
    sendWaitingAccept: "Warten auf Annahme durch dein Telefon…",
    sendWaitingAppOpen: "Öffne Reflecto auf deinem Telefon zum Annehmen",
    sendWaitingAppOpenHint:
      "Dein Telefon kann keine Push-Benachrichtigungen empfangen und zeigt diese Datei erst an, wenn Reflecto geöffnet ist.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Teil ${partNumber} von ${partCount} wird hochgeladen…`,
    sendDone: "Gesendet.",
    sendFailedRejected: "Dein Telefon hat die Datei abgelehnt.",
    sendFailedNoAnswer: "Keine Antwort von deinem Telefon.",
    sendFailedUploadFailed: "Upload fehlgeschlagen. Bitte versuche es erneut.",
    sendFailedCancelled: "Abgebrochen.",
    sendFailedGeneric: "Etwas ist schiefgelaufen. Bitte versuche es erneut.",
    sendTooLarge: (maxMiB: number): string => `Die Datei ist zu groß (max. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Lass diesen Tab geöffnet, bis die Übertragung abgeschlossen ist.",
    sendAnother: "Weitere Datei senden",
    sendNoDevice: "Kopple zuerst ein Telefon, um Dateien zu senden.",
    sendCancel: "Abbrechen",
    sendPeerProgress: (chunksDone: number): string =>
      `Das Telefon hat ${chunksDone} Blöcke empfangen.`,

    receiveTitle: "Eingehende Dateien",
    receiveOfferFrom: (sender: string): string => `${sender} möchte dir eine Datei senden`,
    receiveAccept: "Annehmen",
    receiveDecline: "Ablehnen",
    receiveReceiving: (n: number, total: number): string =>
      `Teil ${n} von ${total} wird empfangen…`,
    receiveDone: "Gespeichert.",
    receiveFailed: "Übertragung fehlgeschlagen.",
    receiveCancelled: "Abgebrochen.",
    receiveCancel: "Abbrechen",

    errorChunkAuthFail:
      "Übertragung fehlgeschlagen – die Daten konnten nicht verifiziert werden. Versuche es erneut.",
    errorHashMismatch:
      "Übertragung fehlgeschlagen – die Datei hat die Integritätsprüfung nicht bestanden. Versuche es erneut.",
    errorStorageFull: "Nicht genug Speicherplatz für diese Übertragung.",
    errorExpired: "Übertragung abgelaufen.",
    errorQuotaExceeded: "Tägliches Übertragungslimit erreicht – versuche es morgen erneut.",
    errorInternal: "Übertragung fehlgeschlagen – etwas ist schiefgelaufen.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct} %`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Ausblenden",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Übertragungsfortschritt",
    outgoing: (n: number): string => `Ausgehend (${n})`,
    incoming: (n: number): string => `Eingehend (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Alle annehmen",
    dropTitle: "Hierher ziehen und ablegen",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Dateien bis ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Hierher ziehen und ablegen oder eine Datei zum Senden auswählen",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Direkt",
    transportDirectHint: "Direkt zwischen Geräten gesendet — nie auf unseren Servern gespeichert.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Über Server",
    transportRelayHint:
      "Über unsere Server geleitet, weiterhin Ende-zu-Ende-verschlüsselt — wir können es nicht lesen.",
    directBadge: "Direkt",
  },
  time: {
    justNow: "gerade eben",
    minutesAgo: (count: number): string => `vor ${count} Min.`,
    hoursAgo: (count: number): string => `vor ${count} Std.`,
  },
} satisfies MessagesShape;
